import React, { useState, useEffect, useCallback } from "react";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import { themeAtom, isAdminAtom } from "../-Atoms";
import { db, doc, updateDoc } from "../firebaseConfig";

const UniversalTextField = ({
  collectionName,
  docId,
  fieldName,
  value = "",
  label,
  variant = "filled",
  multiline = false,
  rows = 1,
  disabled = false,
  required = false,
  error = false,
  helperText = "",
  placeholder = "",
  type = "text",
  inputProps = {},
  sx = {},
  onCallback = null,
  setHasUnsavedChanges = null,
  adminOnly = false,
  noPadding = false,
  ...otherProps
}) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);

  const [fieldValue, setFieldValue] = useState(value);

  useEffect(() => {
    setFieldValue(value);
  }, [value]);

  const saveToFirestore = useCallback(
    async (newValue) => {
      if (adminOnly && !isAdmin) return;
      if (!collectionName || !docId || !fieldName) {
        const missingFields = [];
        if (!collectionName) missingFields.push("collectionName");
        if (!docId) missingFields.push("docId");
        if (!fieldName) missingFields.push("fieldName");

        console.error(
          "Missing required props for UniversalTextField:",
          missingFields
        );
        return { error: "Missing required props", missingFields };
      }
      try {
        const updateData = { [fieldName]: newValue || null };

        await updateDoc(doc(db, collectionName, docId), updateData);

        if (setHasUnsavedChanges) {
          setHasUnsavedChanges(false);
        }

        // Call the callback if provided
        if (onCallback && typeof onCallback === "function") {
          onCallback(newValue, {
            collectionName,
            docId,
            fieldName,
          });
        }
      } catch (error) {
        console.error(`Error updating ${fieldName}:`, error);
      }
    },
    [
      collectionName,
      docId,
      fieldName,
      isAdmin,
      adminOnly,
      setHasUnsavedChanges,
      onCallback,
    ]
  );

  const handleChange = (event) => {
    const newValue = event.target.value;

    if (adminOnly && !isAdmin) return;

    setFieldValue(newValue);

    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  };

  const handleBlur = () => {
    if (adminOnly && !isAdmin) return;

    if (fieldValue !== value) {
      // If Firebase props are missing but we have a callback, call it directly
      if ((!collectionName || !docId || !fieldName) && onCallback && typeof onCallback === "function") {
        onCallback(fieldValue);
        if (setHasUnsavedChanges) {
          setHasUnsavedChanges(false);
        }
      } else {
        saveToFirestore(fieldValue);
      }
    } else if (setHasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  };

  const handleFocus = () => {
    if (adminOnly && !isAdmin) return;

    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  const defaultSx = {
    marginTop: 2,
    position: "relative",
    "& .MuiInputLabel-root": {
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
      fontWeight: 500,
      fontSize: "0.95rem",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, 7px) scale(0.75)",
      },
    },
    "& .MuiFilledInput-root": {
      borderRadius: "8px",
      backgroundColor:
        theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(250,250,250,0.9)",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        backgroundColor:
          theme === "dark" ? "rgba(55,55,55,0.8)" : "rgba(255,255,255,1)",
      },
      "&.Mui-focused": {
        backgroundColor:
          theme === "dark" ? "rgba(60,60,60,0.9)" : "rgba(255,255,255,1)",
        boxShadow:
          theme === "dark"
            ? "0 0 0 2px rgba(100,100,255,0.5)"
            : "0 0 0 2px rgba(100,100,255,0.3)",
      },
    },
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      "&.Mui-focused": {
        backgroundColor:
          theme === "dark" ? "rgba(45,45,45,0.9)" : "rgba(255,255,255,1)",
        boxShadow:
          theme === "dark"
            ? "0 0 0 2px rgba(100,100,255,0.5)"
            : "0 0 0 2px rgba(100,100,255,0.3)",
      },
    },
    ...sx,
  };

  return (
    <FormControl fullWidth sx={defaultSx} variant={variant}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: multiline ? 2 : 0,
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: noPadding ? 0 : multiline ? 2 : 1.5,
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <TextField
              fullWidth
              label={label}
              value={fieldValue}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              variant={variant}
              multiline={multiline}
              rows={multiline ? rows : undefined}
              disabled={disabled || (adminOnly && !isAdmin)}
              required={required}
              error={error}
              helperText={helperText}
              placeholder={placeholder}
              type={type}
              inputProps={inputProps}
              InputProps={{
                disableUnderline: true,
                ...otherProps.InputProps,
              }}
              {...otherProps}
            />
          </motion.div>
        </motion.div>
      </Box>
    </FormControl>
  );
};

export default UniversalTextField;
